/**
 * Crew Highlights Duck
 *
 * Fetches recent activity (bids, linkups, posts) from user's crews for the landing page
 */

import { createAction, createSlice } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import { fetchCrewBids } from './bids.duck';
import { fetchLinkups, getLinkups } from './linkups.duck';
import { getCrewHighlights as getCrewHighlightsFromApi } from '../util/api';

// ================ Action types ================ //

export const FETCH_CREW_HIGHLIGHTS_REQUEST = 'app/crewHighlights/FETCH_CREW_HIGHLIGHTS_REQUEST';
export const FETCH_CREW_HIGHLIGHTS_SUCCESS = 'app/crewHighlights/FETCH_CREW_HIGHLIGHTS_SUCCESS';
export const FETCH_CREW_HIGHLIGHTS_ERROR = 'app/crewHighlights/FETCH_CREW_HIGHLIGHTS_ERROR';

export const CLEAR_CREW_HIGHLIGHTS = 'app/crewHighlights/CLEAR_CREW_HIGHLIGHTS';

// ================ Reducer ================ //

export const initialState = {
  highlightsByCrewId: {}, // { crewId: { bid, linkup, post } }
  fetchInProgress: false,
  fetchError: null,
  lastFetchTime: null,
  lastFetchByCrewId: {},
};

const fetchCrewHighlightsRequestAction = createAction(FETCH_CREW_HIGHLIGHTS_REQUEST);
const fetchCrewHighlightsSuccessAction = createAction(FETCH_CREW_HIGHLIGHTS_SUCCESS);
const fetchCrewHighlightsErrorAction = createAction(FETCH_CREW_HIGHLIGHTS_ERROR);
const clearCrewHighlightsAction = createAction(CLEAR_CREW_HIGHLIGHTS);

const crewHighlightsSlice = createSlice({
  name: 'crewHighlights',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCrewHighlightsRequestAction, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchCrewHighlightsSuccessAction, (state, action) => {
        const highlights = action.payload.highlights || {};
        const fetchedAt = Date.now();
        state.highlightsByCrewId = {
          ...state.highlightsByCrewId,
          ...highlights,
        };
        state.lastFetchByCrewId = {
          ...state.lastFetchByCrewId,
          ...Object.keys(highlights).reduce((acc, crewId) => {
            acc[crewId] = fetchedAt;
            return acc;
          }, {}),
        };
        state.fetchInProgress = false;
        state.lastFetchTime = fetchedAt;
      })
      .addCase(fetchCrewHighlightsErrorAction, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })
      .addCase(clearCrewHighlightsAction, () => initialState);
  },
});

export default crewHighlightsSlice.reducer;

// ================ Action creators ================ //

export const fetchCrewHighlightsRequest = () => fetchCrewHighlightsRequestAction();

export const fetchCrewHighlightsSuccess = highlights =>
  fetchCrewHighlightsSuccessAction({ highlights });

export const fetchCrewHighlightsError = error => fetchCrewHighlightsErrorAction(error);

export const clearCrewHighlights = () => clearCrewHighlightsAction();

// ================ Thunks ================ //

const CREW_HIGHLIGHTS_CACHE_DURATION = 2 * 60 * 1000;
const crewHighlightsRequests = new Map();

/**
 * Fetch recent highlights (1 bid, 1 linkup, 1 post) for multiple crews
 * @param {Array<string>} crewIds - Array of crew IDs to fetch highlights for
 */
export const fetchCrewHighlights = crewIds => async (dispatch, getState, sdk) => {
  const uniqueCrewIds = Array.from(new Set((crewIds || []).map(crewId => String(crewId))));

  if (uniqueCrewIds.length === 0) {
    return Promise.resolve({});
  }

  const state = getState();
  const now = Date.now();
  const cachedHighlights = state.crewHighlights?.highlightsByCrewId || {};
  const lastFetchByCrewId = state.crewHighlights?.lastFetchByCrewId || {};
  const crewsToFetch = uniqueCrewIds.filter(crewId => {
    const cachedCrewHighlights = cachedHighlights[crewId];
    const lastFetched = lastFetchByCrewId[crewId];
    return (
      !cachedCrewHighlights || !lastFetched || now - lastFetched > CREW_HIGHLIGHTS_CACHE_DURATION
    );
  });

  if (crewsToFetch.length === 0) {
    return Promise.resolve(
      uniqueCrewIds.reduce((acc, crewId) => {
        acc[crewId] = cachedHighlights[crewId] || { bid: null, linkup: null, post: null };
        return acc;
      }, {})
    );
  }

  const requestKey = crewsToFetch.sort().join('|');
  if (crewHighlightsRequests.has(requestKey)) {
    return crewHighlightsRequests.get(requestKey);
  }

  dispatch(fetchCrewHighlightsRequest());

  const requestPromise = (async () => {
    const response = await getCrewHighlightsFromApi(crewsToFetch);
    const highlights = response?.highlights || {};

    dispatch(fetchCrewHighlightsSuccess(highlights));
    return uniqueCrewIds.reduce((acc, crewId) => {
      acc[crewId] = highlights[crewId] ||
        cachedHighlights[crewId] || {
          bid: null,
          linkup: null,
          post: null,
        };
      return acc;
    }, {});
  })();

  crewHighlightsRequests.set(requestKey, requestPromise);

  try {
    return await requestPromise;
  } catch (error) {
    console.error('[crewHighlights] Error fetching crew highlights:', error);
    dispatch(fetchCrewHighlightsError(storableError(error)));
    throw error;
  } finally {
    crewHighlightsRequests.delete(requestKey);
  }
};

/**
 * Fetch highlights for a single crew
 * @param {string} crewId - The crew ID
 */
export const fetchSingleCrewHighlights = crewId => async (dispatch, getState, sdk) => {
  return dispatch(fetchCrewHighlights([crewId]));
};

/**
 * Fetch all bids and linkups for user's crews and populate Redux
 * This should be called on login to pre-populate data for the Crew highlights section
 * @param {string} userId - User ID
 */
export const fetchAllCrewBidsAndLinkups = userId => async (dispatch, getState) => {
  if (!userId) {
    return Promise.resolve({ bids: [], linkups: [] });
  }

  try {
    const state = getState();

    // Get user's crews
    const userCrews = state.crews?.userCrews || [];

    if (userCrews.length === 0) {
      return Promise.resolve({ bids: [], linkups: [] });
    }

    const crewIds = userCrews.map(crew => String(crew.id));
    const crewsMap = userCrews.reduce((acc, crew) => {
      acc[String(crew.id)] = crew;
      return acc;
    }, {});

    // Fetch bids for all crews
    const bidsPromises = crewIds.map(async crewId => {
      try {
        const crew = crewsMap[crewId];
        const crewName = crew?.crew_name || crew?.name;
        const bidsResult = await dispatch(fetchCrewBids(crewName || crewId));
        const bids = Array.isArray(bidsResult) ? bidsResult : [];
        return bids.map(bid => ({ ...bid, crewId }));
      } catch (error) {
        console.error(
          `[fetchAllCrewBidsAndLinkups] Error fetching bids for crew ${crewId}:`,
          error
        );
        return [];
      }
    });

    // Fetch linkups for all crews
    const linkupsPromises = crewIds.map(async crewId => {
      try {
        const crew = crewsMap[crewId];
        const crewName = crew?.crew_name || crew?.name;
        if (!crewName) return [];

        await dispatch(fetchLinkups({ crew_name: crewName, limit: 10 }));
        // Get linkups from state after dispatch
        const updatedState = getState();
        const allLinkupsResult = getLinkups(updatedState);
        const allLinkups = Array.isArray(allLinkupsResult) ? allLinkupsResult : [];
        // Filter linkups by crew ID or crew name
        const crewLinkups = allLinkups.filter(l => {
          const linkupCrewId = String(l.crewId || l.crew_id || '');
          const linkupCrewName = l.crew_name || '';
          return linkupCrewId === String(crewId) || linkupCrewName === crewName;
        });
        return crewLinkups.map(linkup => ({ ...linkup, crewId }));
      } catch (error) {
        console.error(
          `[fetchAllCrewBidsAndLinkups] Error fetching linkups for crew ${crewId}:`,
          error
        );
        return [];
      }
    });

    // Wait for all fetches to complete
    const [bidsArrays, linkupsArrays] = await Promise.all([
      Promise.all(bidsPromises),
      Promise.all(linkupsPromises),
    ]);

    // Flatten arrays
    const allBids = bidsArrays.flat();
    const allLinkups = linkupsArrays.flat();

    return {
      bids: allBids,
      linkups: allLinkups,
      crewsMap,
    };
  } catch (error) {
    console.error('[fetchAllCrewBidsAndLinkups] Error:', error);
    return { bids: [], linkups: [], crewsMap: {} };
  }
};
